import React, { useEffect, useRef, useState } from 'react';
import {
  Box,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Divider,
  Alert,
  IconButton,
} from '@mui/material';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import CloseIcon from '@mui/icons-material/Close';
import type { Campaign, Scenario } from '../../domain/mailingDomain';
import { SMS_PAGE } from '../../theme/smsPageDesign';
import { apiDelete } from '../../api/client';
import { useTranslation } from 'react-i18next';
import CampaignFormPanel, { type CampaignFormPanelHandle } from './CampaignFormPanel';

export type CampaignSettingsDialogProps = {
  open: boolean;
  onClose: () => void;
  campaign: Campaign;
  scenarios: Scenario[];
  onSaved: () => void;
  onDeleted: () => void;
};

const CampaignSettingsDialog: React.FC<CampaignSettingsDialogProps> = ({
  open,
  onClose,
  campaign,
  scenarios,
  onSaved,
  onDeleted,
}) => {
  const { t } = useTranslation();
  const formRef = useRef<CampaignFormPanelHandle>(null);
  const [footerSaving, setFooterSaving] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const cabinetAborted = campaign.status === 'aborted';

  useEffect(() => {
    if (!open) return;
    setErr(null);
    setConfirmOpen(false);
  }, [open, campaign.id]);

  const handleSave = async () => {
    setErr(null);
    setFooterSaving(true);
    try {
      const ok = (await formRef.current?.save()) ?? false;
      if (ok) onClose();
    } catch (e) {
      setErr((e as Error).message);
    } finally {
      setFooterSaving(false);
    }
  };

  const remove = async () => {
    setDeleting(true);
    setErr(null);
    try {
      await apiDelete(`/api/campaigns/${campaign.id}`);
      setConfirmOpen(false);
      onClose();
      onDeleted();
    } catch (e) {
      setErr((e as Error).message);
    } finally {
      setDeleting(false);
    }
  };

  return (
    <>
      <Dialog
        open={open}
        onClose={onClose}
        maxWidth="lg"
        fullWidth
        scroll="paper"
        PaperProps={{ sx: { borderRadius: 2, maxHeight: 'calc(100dvh - 24px)' } }}
      >
        <DialogTitle sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 1, pr: 1 }}>
          <Box sx={{ minWidth: 0, pt: 0.5 }}>
            <Typography variant="h6" fontWeight={600}>
              {t('campaignSettings.dialogTitle')}
            </Typography>
          </Box>
          <IconButton aria-label={t('common.close')} onClick={onClose} size="small">
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers sx={{ px: { xs: 1.5, sm: 2 }, py: 1 }}>
          {err && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {err}
            </Alert>
          )}
          {open ? (
            <CampaignFormPanel
              ref={formRef}
              campaignEditId={campaign.id}
              scenarios={scenarios}
              onSaved={onSaved}
            />
          ) : null}

          <Divider sx={{ my: 2 }} />

          <Typography variant="subtitle2" fontWeight={600} gutterBottom>
            {t('campaignSettings.sectionManagement')}
          </Typography>
          <Button
            variant="outlined"
            color="error"
            startIcon={<DeleteOutlineIcon />}
            onClick={() => setConfirmOpen(true)}
            disabled={campaign.status === 'running' || cabinetAborted}
            sx={{
              borderRadius: '8px',
              textTransform: 'none',
              borderColor: SMS_PAGE.errorRed,
              color: SMS_PAGE.errorRed,
              '&:hover': { borderColor: SMS_PAGE.errorRed, bgcolor: 'rgba(255,0,4,0.06)' },
            }}
          >
            {t('campaignSettings.deleteCampaign')}
          </Button>
          {cabinetAborted && (
            <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 1 }}>
              {t('campaignSettings.cannotDeleteCabinetAborted')}
            </Typography>
          )}
          {campaign.status === 'running' && !cabinetAborted && (
            <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 1 }}>
              {t('campaignSettings.stopFirstToDelete')}
            </Typography>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2, gap: 1 }}>
          <Button onClick={onClose} sx={{ textTransform: 'none' }}>
            {t('common.cancel')}
          </Button>
          <Button
            variant="contained"
            onClick={() => void handleSave()}
            disabled={footerSaving || cabinetAborted}
            sx={{ borderRadius: '8px', textTransform: 'none' }}
          >
            {footerSaving ? t('common.saving') : t('common.save')}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={confirmOpen} onClose={() => !deleting && setConfirmOpen(false)}>
        <DialogTitle>{t('campaignSettings.confirmDeleteTitle')}</DialogTitle>
        <DialogContent>
          <Typography variant="body2">
            {t('campaignSettings.confirmDeleteBody', { name: campaign.name })}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmOpen(false)} disabled={deleting}>
            {t('common.cancel')}
          </Button>
          <Button color="error" variant="contained" onClick={() => remove()} disabled={deleting}>
            {t('common.delete')}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default CampaignSettingsDialog;
